import {
  Modal,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material'
import CRMDataGrid from '@/components/Table/CRM/CRMDataGrid'
import TaskCreatorEdit from '@/assets/crm/TaskCreatorEdit.svg'
import CloseIcon from '@mui/icons-material/Close'
import DetailsAreaTaskCreator from './DetailsArea.task.creator'
import FileAreaTaskCreator from './FileArea.task.creator'
import CommentsAreaTaskCreator from './CommentsArea.task.creator'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCRMStore } from '@/store/crm/crmStore'
import CreateTask from '@assets/crm/CreateTask.svg'
import { useAppTheme } from '@/theme/ThemeContext'
import {
  APICrmFile,
  APICrmTaskDetails,
  addCrmTaskComment,
  createCrmTask,
  downloadCrmTaskFile,
  getCrmErrorMessage,
  getCrmTask,
  updateCrmTask,
  uploadCrmTaskFile,
} from '@/services/crmService'
import {
  crmQueryKeys,
  useCrmAccountId,
  useCrmReference,
  useRefreshCrmTasks,
} from '@/hooks/useCrm'
import { buildCreateRequest, buildUpdatePatch } from '@/utils/crmTask'
import type {
  CrmGridRow,
  CrmRowChangeHandler,
} from '@/components/features/config/columns/consumers/column.crm.config'

/** Row id of the not-yet-created task in create mode. */
const NEW_TASK_ROW_ID = -1
const DEFAULT_PRIORITY = 0

const emptyDraft = (meterNumber?: string): CrmGridRow => ({
  id: NEW_TASK_ROW_ID,
  meterNumber: meterNumber ?? '',
  priority: DEFAULT_PRIORITY,
  dueAt: null,
})

const TaskCreator = ({
  open,
  handleClose,
  meterNumber,
}: {
  open: boolean
  handleClose: () => void
  /** Consumer tab: the meter number is prefilled and locked. */
  meterNumber?: string
}) => {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const { crmTaskCreatorModal, showToast } = useCRMStore()
  const accountId = useCrmAccountId()
  const queryClient = useQueryClient()
  const refreshTasks = useRefreshCrmTasks(accountId)
  const { data: reference, isError: noCrm } = useCrmReference(accountId)

  const isEdit = crmTaskCreatorModal.mode === 'edit'
  const taskId = isEdit ? crmTaskCreatorModal.data?.taskId : undefined
  const taskKey = crmQueryKeys.task(accountId ?? '', taskId ?? 0)

  const {
    data: task,
    isLoading: taskLoading,
    isError: taskError,
  } = useQuery({
    queryKey: taskKey,
    queryFn: () => getCrmTask(accountId as string, taskId as number),
    enabled: open && isEdit && !!accountId && taskId != null,
  })

  const [draft, setDraft] = useState<CrmGridRow>(() => emptyDraft(meterNumber))
  const [description, setDescription] = useState('')
  const [pendingComment, setPendingComment] = useState('')
  const [saving, setSaving] = useState(false)
  const [commentSaving, setCommentSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const initializedFor = useRef<string | null>(null)

  // Seed the form once per opening: empty in create mode, from the loaded task in edit mode.
  useEffect(() => {
    if (!open) {
      initializedFor.current = null
      return
    }
    const key = isEdit ? `edit-${taskId}` : 'create'
    if (initializedFor.current === key) return
    if (isEdit) {
      if (!task || task.id !== taskId) return
      setDraft({ ...task })
      setDescription(task.description ?? '')
    } else {
      setDraft(emptyDraft(meterNumber))
      setDescription('')
    }
    setPendingComment('')
    initializedFor.current = key
  }, [open, isEdit, taskId, task, meterNumber])

  const handleDraftChange = useCallback<CrmRowChangeHandler>((_, patch) => {
    setDraft(prev => {
      const next: CrmGridRow = { ...prev }
      if (patch.typeId !== undefined) next.typeId = patch.typeId
      if (patch.meterNumber !== undefined) next.meterNumber = patch.meterNumber
      if (patch.assigneeId !== undefined) next.assigneeId = patch.assigneeId
      if (patch.statusId !== undefined) next.statusId = patch.statusId
      if (patch.priority !== undefined) next.priority = patch.priority
      if (patch.dueAt !== undefined) next.dueAt = patch.dueAt
      if (patch.clearDueAt) next.dueAt = null
      return next
    })
  }, [])

  const closeModal = () => {
    if (taskId != null) queryClient.removeQueries({ queryKey: taskKey })
    handleClose()
  }

  const applyDetails = (details: APICrmTaskDetails | undefined) => {
    if (details) queryClient.setQueryData(taskKey, details)
  }

  const handleSave = async () => {
    if (!accountId || saving) return
    const comment = pendingComment.trim()

    if (!isEdit) {
      const request = buildCreateRequest(draft, description, comment)
      if (!request) {
        showToast('יש לבחור סוג משימה ומשויך', 'error')
        return
      }
      setSaving(true)
      try {
        await createCrmTask(accountId, request)
        showToast('המשימה נוצרה')
        await refreshTasks()
        closeModal()
      } catch (error) {
        showToast(
          await getCrmErrorMessage(error, 'יצירת המשימה נכשלה'),
          'error'
        )
      } finally {
        setSaving(false)
      }
      return
    }

    if (!task || taskId == null) return
    const patch = buildUpdatePatch(task, draft, description, comment)

    if (Object.keys(patch).length === 0) {
      closeModal()
      return
    }
    setSaving(true)
    try {
      await updateCrmTask(accountId, taskId, patch)
      showToast('המשימה עודכנה')
      await refreshTasks()
      closeModal()
    } catch (error) {
      showToast(await getCrmErrorMessage(error, 'עדכון המשימה נכשל'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleAddComment = async () => {
    const comment = pendingComment.trim()
    if (!accountId || taskId == null || !comment) return
    setCommentSaving(true)
    try {
      applyDetails(await addCrmTaskComment(accountId, taskId, { comment }))
      setPendingComment('')
      await refreshTasks()
    } catch (error) {
      showToast(await getCrmErrorMessage(error, 'הוספת ההערה נכשלה'), 'error')
    } finally {
      setCommentSaving(false)
    }
  }

  const handleUpload = async (file: File) => {
    if (!accountId || taskId == null) return
    setUploading(true)
    try {
      applyDetails(await uploadCrmTaskFile(accountId, taskId, file))
      showToast('הקובץ צורף')
      await refreshTasks()
    } catch (error) {
      showToast(await getCrmErrorMessage(error, 'העלאת הקובץ נכשלה'), 'error')
    } finally {
      setUploading(false)
    }
  }

  const handleDownload = async (file: APICrmFile) => {
    if (!accountId || taskId == null) return
    try {
      await downloadCrmTaskFile(accountId, taskId, file)
    } catch (error) {
      showToast(await getCrmErrorMessage(error, 'הורדת הקובץ נכשלה'), 'error')
    }
  }

  const handleDownloadAll = async () => {
    for (const file of task?.files ?? []) {
      await handleDownload(file)
    }
  }

  const draftRows = useMemo(() => [draft], [draft])

  const BG_COLOR = isDarkMode ? '#202B40' : '#FAFBFD'
  const BORDER_COLOR = isDarkMode ? '#374151' : 'rgba(50, 59, 119, 0.10)'
  const TEXT_COLOR = isDarkMode ? '#FFFFFF' : '#003F61'

  const editNotReady = isEdit && (taskLoading || taskError || !task)

  return (
    <Modal
      open={open}
      onClose={closeModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={{
        background: 'rgba(50, 59, 119, 0.10)',
        backdropFilter: 'blur(6.800000190734863px)',
      }}
    >
      <Box
        sx={{
          borderRadius: '8px',
          border: `1px solid ${BORDER_COLOR}`,
          background: BG_COLOR,
          width: 'min(1340px, 96vw)',
          boxSizing: 'border-box',
          margin: '0 auto',
          marginTop: '8vh',
          px: '38px',
          py: '36px',
          maxHeight: '84vh',
          overflowY: 'auto',
          '::-webkit-scrollbar': { display: 'none' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: `1px solid ${BORDER_COLOR}`,
            paddingBottom: '24px',
          }}
        >
          <IconButton onClick={closeModal} aria-label="סגור">
            <CloseIcon sx={{ color: TEXT_COLOR }} />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <Typography
              sx={{
                fontFamily: 'Rubik, sans-serif',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '100%',
                color: TEXT_COLOR,
              }}
            >
              {crmTaskCreatorModal.mode === 'create'
                ? 'יצירת משימה חדשה'
                : 'צפייה ועריכה'}{' '}
            </Typography>

            <img
              src={
                crmTaskCreatorModal.mode === 'create'
                  ? CreateTask
                  : TaskCreatorEdit
              }
              alt="TaskCreatorEdit"
              width={15}
              height={15}
              style={{
                filter: isDarkMode ? 'brightness(0) invert(1)' : 'none',
              }}
            />
          </Box>
        </Box>
        {editNotReady ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minWidth: 600,
              minHeight: 200,
            }}
          >
            {taskError ? (
              <Typography
                sx={{ fontFamily: 'Rubik, sans-serif', color: '#FE595A' }}
              >
                טעינת המשימה נכשלה
              </Typography>
            ) : (
              <CircularProgress size={32} sx={{ color: '#72A4EB' }} />
            )}
          </Box>
        ) : (
          <>
            {noCrm && (
              <Typography
                role="alert"
                dir="rtl"
                sx={{
                  fontFamily: 'Rubik, sans-serif',
                  fontSize: '14px',
                  color: '#FE595A',
                  pt: '16px',
                  textAlign: 'right',
                }}
              >
                ה-CRM אינו זמין באתר זה
              </Typography>
            )}
            <Box
              sx={{
                borderBottom: `1px solid ${BORDER_COLOR}`,
                pt: '16px',
              }}
            >
              <CRMDataGrid
                rows={draftRows}
                reference={reference}
                onRowChange={handleDraftChange}
                draft
                meterLocked={!!meterNumber}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                my: '30px',
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  borderRight: `1px solid ${BORDER_COLOR}`,
                  marginRight: '9px',
                }}
              >
                <CommentsAreaTaskCreator
                  comments={task?.comments ?? []}
                  value={pendingComment}
                  onChange={setPendingComment}
                  onSubmit={isEdit ? handleAddComment : undefined}
                  submitting={commentSaving}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <DetailsAreaTaskCreator
                  value={description}
                  onChange={setDescription}
                />
                <FileAreaTaskCreator
                  files={isEdit ? (task?.files ?? []) : []}
                  canUpload={isEdit}
                  uploading={uploading}
                  onUpload={handleUpload}
                  onDownload={handleDownload}
                  onDownloadAll={handleDownloadAll}
                />
              </Box>
            </Box>
            <Box
              sx={{
                borderRadius: '4.5px',
                border: '1px solid #E4E7EB',
                background: '#72A4EB',
                width: 'fit-content',
                padding: '0 16px',
                height: '38px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                cursor: saving ? 'default' : 'pointer',
                opacity: saving ? 0.7 : 1,
              }}
              onClick={handleSave}
            >
              {saving && (
                <CircularProgress size={14} sx={{ color: '#F8FAFD' }} />
              )}
              <Typography
                sx={{
                  color: '#F8FAFD',
                  textAlign: 'right',
                  /* rubik 14 bold */
                  fontFamily: 'Rubik, sans-serif',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: '100%',
                }}
              >
                שמירה
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  )
}

export default TaskCreator
